import React, { FC } from 'react';
import { Box, Text, Heading, Button } from '@wix/design-system';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useBi } from '@components/Hooks/useBi';
import { getWixLoginUrl } from '@utils/urlUtils';
import BriefPromoGif from '@components/Images/brief-promo/brief-promo-card-gif.gif';
import { briefPromoCardDataHooks } from './BriefPromoCard.dataHooks';
import s from './BriefPromoCard.module.scss';

type Props = {
  isMobileView: boolean;
};

const BriefPromoCard: FC<Props> = ({ isMobileView }) => {
  const { t } = useTranslation();
  const {
    state: { language, origin, entry },
  } = useExploreContext();
  const { userDetails, host } = useHeaderContext();
  const BRIEF_URL = `${host}/marketplace/brief-auto-match?entry=explore&origin=results-page-top`;
  const { sessionId } = useBiSessionContext();
  const logger = useBi();

  const matchURL =
    userDetails == null
      ? `${getWixLoginUrl(language)}&redirectTo=${encodeURIComponent(
          BRIEF_URL,
        )}`
      : BRIEF_URL;

  return (
    <Box
      direction="vertical"
      backgroundColor="#fff"
      height="100%"
      border="1px solid #dfe5eb"
      borderRadius="8px"
      className={s.container}
      dataHook={briefPromoCardDataHooks.boxContainer()}
    >
      <Box
        align="center"
        padding={isMobileView ? '18px 24px 0' : '24px 36px 0'}
        className={s.imageSection}
      >
        <img
          src={BriefPromoGif.src}
          className={s.gif}
          width={isMobileView ? 136 : 198}
          alt=""
          data-hook={briefPromoCardDataHooks.gif()}
        />
      </Box>

      <Box
        direction="vertical"
        align="center"
        textAlign="center"
        padding={isMobileView ? '18px 24px 24px' : '24px 36px 18px'}
      >
        <Heading size={isMobileView ? 'medium' : 'small'} as="h3">
          {t('explore-page.brief-promo-card.title')}
        </Heading>
        <Box marginTop="6px">
          <Text size="small" weight="thin" secondary>
            {t('explore-page.brief-promo-card.subtitle')}
          </Text>
        </Box>
        <Box marginTop="18px">
          <Button
            skin="standard"
            as="a"
            size="small"
            href={matchURL}
            target="_blank"
            dataHook={briefPromoCardDataHooks.cta()}
            onClick={() => {
              logger.report(
                exploreResultsPageGenericClick({
                  clickType: 'Get Matched',
                  sessionId,
                  origin,
                  entry,
                  exploreVersion: 'V2',
                }),
              );
            }}
          >
            {t('explore-page.brief-promo-card.cta')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BriefPromoCard;
